import React, { useState, useEffect, useCallback, useMemo, useRef, } from "react";
import {
  Box, IconButton, Button, Typography, Chip, Alert, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Menu, MenuItem, Switch, FormControlLabel, Snackbar,
  Drawer, Tooltip, Stack, useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ExpandMore, ChevronRight, Add, Edit, Delete, MoreVert, AddBox,
  Payment, UnfoldMore, UnfoldLess, Close, InfoOutlined, FileDownload,
  UploadFile,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import contractItemsApi from "../../services/contractItems.service";
import ContractItemModal from "./ContractItemModal";
import ContractItemsImportDialog from "./ContractItemsImportDialog";
import { formatCurrency, formatDate, formatAmount } from "utils/formatters";
import { getToken, getUser } from "services/auth.service";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import { MaterialReactTableConfig } from "components/common/table/TableConfig";
import exportContractItemsExcel from "../../utils/exportContractItemsExcel";
import "./ContractItemsTable.css";
import { AppButton } from "components/common/buttons";

// Custom number formatting functions for payment columns
// Định dạng tiền tệ: dùng dấu "." phân tách hàng nghìn, không có đơn vị
// const formatPaymentAmount = (amount) => {
//   if (!amount || amount === 0) return "";
//   return new Intl.NumberFormat("de-DE").format(amount); // Sử dụng locale Đức để có dấu "." phân tách nghìn
// };

// Định dạng số lượng: dùng dấu "," phân tách thập phân
const formatPaymentQuantity = (quantity) => {
  if (!quantity || quantity === 0) return "";
  // Sử dụng locale Việt Nam để có dấu "," phân tách thập phân
  return new Intl.NumberFormat("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(quantity);
};

const INLINE_EDITABLE_FIELDS = [
  "itemName",
  "unit",
  "contractQuantity",
  // "unitPrice",
  "status",
  "difficultyLevel",
];

const STATUS_OPTIONS = [
  "Chưa bắt đầu",
  "Đang thực hiện",
  "Hoàn thành",
  "Tạm dừng",
  "Hủy bỏ",
];

const DIFFICULTY_OPTIONS = [1, 2, 3, 4, 5];

const UNIT_OPTIONS = [
  "%",
  "Bộ",
  "Cái",
  "Điểm",
  "Gói",
  "ha",
  "kg",
  "m",
  "m²",
  "Mảnh QĐ",
  "Ngày",
  "Phần",
  "Tấn",
  "Tháng",
];

const normalizeNumberInput = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const rawValue = value.toString().trim();
  let normalized = rawValue;

  if (rawValue.includes(".") && rawValue.includes(",")) {
    normalized = rawValue.replace(/\./g, "").replace(",", ".");
  } else if (rawValue.includes(",")) {
    normalized = rawValue.replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(rawValue)) {
    normalized = rawValue.replace(/\./g, "");
  }

  const numberValue = Number(normalized);
  return Number.isFinite(numberValue) ? numberValue : Number.NaN;
};

// Sort items by ItemCode recursively - moved outside component to prevent re-creation
const sortItemsByCode = (items) => {
  const parseItemCode = (code) => {
    if (!code) return [0];
    return code.split(".").map((part) => parseInt(part) || 0);
  };

  const compareItemCodes = (a, b) => {
    const codeA = parseItemCode(a.itemCode);
    const codeB = parseItemCode(b.itemCode);

    const maxLength = Math.max(codeA.length, codeB.length);
    for (let i = 0; i < maxLength; i++) {
      const partA = codeA[i] || 0;
      const partB = codeB[i] || 0;
      if (partA !== partB) {
        return partA - partB;
      }
    }
    return 0;
  };

  const sortedItems = items.sort(compareItemCodes);

  // Recursively sort children
  return sortedItems.map((item) => ({
    ...item,
    children: item.children ? sortItemsByCode(item.children) : [],
  }));
};

const ContractItemsTable = ({ contractId, contract, onSnackbar }) => {
  const theme = useTheme();
  const tableConfig = useMemo(() => MaterialReactTableConfig(), []);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const hasLoadedRef = useRef(false); // Prevent double loading
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create", "edit", "addChild"
  const [modalParentItem, setModalParentItem] = useState(null);
  const [modalEditItem, setModalEditItem] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedDetailItemId, setSelectedDetailItemId] = useState(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [inlineSaving, setInlineSaving] = useState(false);

  // Payment-related states
  const [showPaymentColumns, setShowPaymentColumns] = useState(false);
  const [paymentProfiles, setPaymentProfiles] = useState([]);
  const [contractPayments, setContractPayments] = useState([]);

  // States cho dialog thông báo không thể tạo con
  const [cannotAddChildDialogOpen, setCannotAddChildDialogOpen] =
    useState(false);
  const [itemCannotAddChild, setItemCannotAddChild] = useState(null);

  // Permission Snackbar state
  const [permissionSnackbar, setPermissionSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  // Helper function để kiểm tra xem item có children hay không
  const isParentItem = useCallback((item) => {
    return item.hasChildren || (item.children && item.children.length > 0);
  }, []);

  const isInlineCellEditable = useCallback(
    (item, field) => {
      if (
        !item ||
        item.isSummaryRow ||
        !INLINE_EDITABLE_FIELDS.includes(field)
      ) {
        return false;
      }

      // Parent rows only expose fields that are already visible in this table.
      if (isParentItem(item) && !["itemName", "unit"].includes(field)) {
        return false;
      }

      return true;
    },
    [isParentItem],
  );

  // Helper function để kiểm tra xem item có phải là leaf node không (có đầy đủ quantity, price, amount)
  const isLeafNode = (item) => {
    return (
      item.contractQuantity &&
      item.contractQuantity > 0 &&
      item.unitPrice &&
      item.unitPrice > 0 &&
      item.contractAmount &&
      item.contractAmount > 0 &&
      item.unit &&
      item.unit.trim() !== ""
    );
  };

  // Helper function để tính tổng thanh toán của hạng mục cha từ các hạng mục con
  const calculateParentPaymentTotal = useCallback(
    (parentItem, paymentProfileId = null) => {
      const getAllChildItems = (item) => {
        let allChildren = [];
        if (item.children && item.children.length > 0) {
          item.children.forEach((child) => {
            if (isParentItem(child)) {
              // Nếu là parent, lấy tiếp các con của nó
              allChildren = allChildren.concat(getAllChildItems(child));
            } else {
              // Nếu là leaf node, thêm vào danh sách
              allChildren.push(child);
            }
          });
        }
        return allChildren;
      };

      const childItems = getAllChildItems(parentItem);

      if (paymentProfileId) {
        // Tính tổng cho một đợt thanh toán cụ thể
        return childItems.reduce((total, child) => {
          const payment = contractPayments.find(
            (p) =>
              p.contractItemId === child.itemId &&
              p.paymentProfileId === paymentProfileId,
          );
          if (payment) {
            const amount =
              payment.paymentAmount && payment.paymentAmount > 0
                ? Math.round(payment.paymentAmount)
                : Math.round(
                  (child.unitPrice || 0) * (payment.paymentQuantity || 0),
                );
            return total + amount;
          }
          return total;
        }, 0);
      } else {
        // Tính tổng lũy kế tất cả các đợt
        return childItems.reduce((total, child) => {
          const childPayments = contractPayments.filter(
            (p) => p.contractItemId === child.itemId,
          );
          const childTotal = childPayments.reduce((sum, payment) => {
            const amount =
              payment.paymentAmount && payment.paymentAmount > 0
                ? Math.round(payment.paymentAmount)
                : Math.round(
                  (child.unitPrice || 0) * (payment.paymentQuantity || 0),
                );
            return sum + amount;
          }, 0);
          return total + childTotal;
        }, 0);
      }
    },
    [contractPayments, isParentItem],
  );

  // Load hierarchical data - stable version for initial load
  const loadContractItems = useCallback(async () => {
    // Prevent double loading
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    try {
      setLoading(true);
      const response =
        await contractItemsApi.getItemsHierarchicalByContractId(contractId);

      if (response.success) {
        // Sort items by ItemCode
        const sortedItems = sortItemsByCode(response.data || []);
        setItems(sortedItems);

        // Auto-expand level 0 items
        const level0Items = new Set();
        sortedItems.forEach((item) => {
          if (item.level_ === 0 && item.hasChildren) {
            level0Items.add(item.itemId);
          }
        });
        setExpandedItems(level0Items);
      } else {
        console.error("Error loading data:", response.message);
      }
    } catch (error) {
      console.error("Error loading contract items:", error);
    } finally {
      setLoading(false);
    }
  }, [contractId]); // Remove onSnackbar dependency to prevent re-execution

  // Load payment profiles
  const loadPaymentProfiles = useCallback(async () => {
    if (!contractId) return [];

    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/PaymentProfile/contract/${contractId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const result = await response.json();
        // Sort by paymentDate
        const sortedProfiles = (result.data || []).sort(
          (a, b) => new Date(a.paymentDate) - new Date(b.paymentDate),
        );
        setPaymentProfiles(sortedProfiles);
        return sortedProfiles;
      } else {
        console.warn("Could not load payment profiles");
        setPaymentProfiles([]);
        return [];
      }
    } catch (error) {
      console.error("Error loading payment profiles:", error);
      setPaymentProfiles([]);
      return [];
    }
  }, [contractId]);

  // Load contract payments
  const loadContractPayments = useCallback(async () => {
    if (!contractId) return [];

    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/ContractPayment/contract/${contractId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const result = await response.json();
        const loadedPayments = result.data || [];
        setContractPayments(loadedPayments);
        return loadedPayments;
      } else {
        console.warn("Could not load contract payments");
        setContractPayments([]);
        return [];
      }
    } catch (error) {
      console.error("Error loading contract payments:", error);
      setContractPayments([]);
      return [];
    }
  }, [contractId]);

  // Simple reload function that bypasses the flag
  const forceReload = useCallback(async () => {
    try {
      const response =
        await contractItemsApi.getItemsHierarchicalByContractId(contractId);

      if (response.success) {
        const sortedItems = sortItemsByCode(response.data || []);
        setItems(sortedItems);
      }
    } catch (error) {
      console.error("Error reloading contract items:", error);
    }
  }, [contractId]);

  useEffect(() => {
    if (contractId) {
      hasLoadedRef.current = false; // Reset flag when contractId changes
      loadContractItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]); // Only depend on contractId, not loadContractItems to prevent double loading

  // Load payment data when payment columns are shown
  useEffect(() => {
    if (showPaymentColumns && contractId) {
      loadPaymentProfiles();
      loadContractPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaymentColumns, contractId]); // Handle expand/collapse
  const handleToggleExpand = useCallback(
    (itemId) => {
      const newExpanded = new Set(expandedItems);
      if (newExpanded.has(itemId)) {
        newExpanded.delete(itemId);
      } else {
        newExpanded.add(itemId);
      }
      setExpandedItems(newExpanded);
    },
    [expandedItems],
  );

  const collectExpandableItemIds = useCallback((items) => {
    const ids = [];
    const collect = (list) => {
      list.forEach((item) => {
        if (item.children && item.children.length > 0) {
          ids.push(item.itemId);
          collect(item.children);
        }
      });
    };
    collect(items);
    return ids;
  }, []);

  const handleExpandAll = useCallback(() => {
    setExpandedItems(new Set(collectExpandableItemIds(items)));
  }, [collectExpandableItemIds, items]);

  const handleCollapseAll = useCallback(() => {
    setExpandedItems(new Set());
  }, []);

  const handleSelectDetailItem = useCallback(
    (item, options = {}) => {
      const { requirePaymentColumns = true } = options;
      if (
        (requirePaymentColumns && !showPaymentColumns) ||
        !item ||
        item.isSummaryRow
      ) {
        return;
      }
      setSelectedDetailItemId(item.itemId);
      setDetailPanelOpen(true);
    },
    [showPaymentColumns],
  );

  const handleCloseDetailPanel = useCallback(() => {
    setShowPaymentColumns(false);
    setDetailPanelOpen(false);
  }, []);

  // Handle menu
  const handleMenuOpen = useCallback((event, item) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedItem(item);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setSelectedItem(null);
  }, []);

  // Handle delete
  const handleDeleteClick = async (item) => {
    try {
      // Check permission first
      const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_ITEM_DELETE);
      if (!result.hasPermission) {
        setPermissionSnackbar({
          open: true,
          message: "Bạn không có quyền xóa hạng mục hợp đồng",
          severity: "warning",
        });
        handleMenuClose();
        return;
      }

      // If has permission, proceed with delete
      setItemToDelete(item);
      setDeleteDialogOpen(true);
      handleMenuClose();
    } catch (error) {
      console.error("Error checking permission:", error);
      setPermissionSnackbar({
        open: true,
        message: "Lỗi kiểm tra quyền truy cập",
        severity: "error",
      });
      handleMenuClose();
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      const response = await contractItemsApi.deleteItem(itemToDelete.itemId);

      if (response.success) {
        onSnackbar?.("Xóa hạng mục thành công", "success");
        forceReload(); // Use force reload to update data
      } else {
        onSnackbar?.(response.message || "Lỗi khi xóa hạng mục", "error");
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      onSnackbar?.("Lỗi khi xóa hạng mục", "error");
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  // Handle add child
  const handleAddChild = async (parentItem) => {
    // Kiểm tra quyền trước
    const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_ITEM_CREATE);
    if (!result.hasPermission) {
      setPermissionSnackbar({
        open: true,
        message: "Bạn không có quyền tạo hạng mục hợp đồng.",
        severity: "warning",
      });
      handleMenuClose();
      return;
    }

    // Kiểm tra xem item có phải là leaf node không (có đầy đủ quantity, price, amount)
    if (isLeafNode(parentItem)) {
      setItemCannotAddChild(parentItem);
      setCannotAddChildDialogOpen(true);
      handleMenuClose();
      return;
    }

    setModalMode("addChild");
    setModalParentItem(parentItem);
    setModalEditItem(null);
    setModalOpen(true);
    handleMenuClose();
  };

  // Handle edit
  const handleEdit = useCallback(async (item) => {
    if (!item) return;

    const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_ITEM_UPDATE);
    if (result.hasPermission) {
      setModalMode("edit");
      setModalParentItem(null);
      setModalEditItem(item);
      setModalOpen(true);
      handleMenuClose();
    } else {
      setPermissionSnackbar({
        open: true,
        message: "Bạn không có quyền chỉnh sửa hạng mục hợp đồng.",
        severity: "warning",
      });
      handleMenuClose();
    }
  }, [handleMenuClose]);

  const normalizeInlineEditValue = useCallback((field, value) => {
    if (field === "contractQuantity" || field === "unitPrice") {
      return normalizeNumberInput(value);
    }

    if (field === "difficultyLevel") {
      const numberValue = Number(value);
      return Number.isInteger(numberValue) ? numberValue : Number.NaN;
    }

    if (field === "itemName" || field === "unit" || field === "status") {
      return value === null || value === undefined ? "" : value.toString().trim();
    }

    return value;
  }, []);

  const validateInlineEditValue = useCallback((field, value) => {
    if (field === "contractQuantity" || field === "unitPrice") {
      if (!Number.isFinite(value) || value < 0) {
        return field === "contractQuantity"
          ? "Khối lượng phải là số hợp lệ và không âm"
          : "Đơn giá phải là số hợp lệ và không âm";
      }
    }

    if (field === "difficultyLevel" && !DIFFICULTY_OPTIONS.includes(value)) {
      return "Mức độ khó khăn không hợp lệ";
    }

    if (field === "status" && !STATUS_OPTIONS.includes(value)) {
      return "Trạng thái không hợp lệ";
    }

    return "";
  }, []);

  const handleInlineEdit = useCallback(
    async ({ item, field, value }) => {
      if (!isInlineCellEditable(item, field)) return;

      const nextValue = normalizeInlineEditValue(field, value);
      const validationMessage = validateInlineEditValue(field, nextValue);
      if (validationMessage) {
        setPermissionSnackbar({
          open: true,
          message: validationMessage,
          severity: "warning",
        });
        await forceReload();
        return;
      }

      const currentValue = normalizeInlineEditValue(field, item[field]);
      if (currentValue === nextValue) return;

      try {
        const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_ITEM_UPDATE);
        if (!result.hasPermission) {
          setPermissionSnackbar({
            open: true,
            message: "Bạn không có quyền chỉnh sửa hạng mục hợp đồng.",
            severity: "warning",
          });
          await forceReload();
          return;
        }

        setInlineSaving(true);

        const nextItem = {
          ...item,
          [field]: nextValue,
        };
        const quantity = nextItem.contractQuantity
          ? Number(nextItem.contractQuantity)
          : null;
        const price = nextItem.unitPrice ? Number(nextItem.unitPrice) : null;
        const contractAmount = quantity && price ? quantity * price : null;

        const submitData = {
          itemName: nextItem.itemName || "",
          description: nextItem.description || "",
          unit: nextItem.unit || "",
          contractQuantity: quantity,
          unitPrice: price,
          contractAmount,
          startDate: nextItem.startDate || null,
          endDate: nextItem.endDate || null,
          status: nextItem.status || "Chưa bắt đầu",
          priority: nextItem.priority || 2,
          completionPercentage: nextItem.completionPercentage || 0,
          difficultyLevel: nextItem.difficultyLevel
            ? parseInt(nextItem.difficultyLevel)
            : 1,
          notes: nextItem.notes || "",
          contractId: parseInt(contractId),
          parentItemId: nextItem.parentItemId || null,
        };

        const response = await contractItemsApi.updateItem(
          nextItem.itemId,
          submitData,
        );

        if (response.success || response.Success) {
          onSnackbar?.("Cập nhật hạng mục thành công", "success");
        } else {
          onSnackbar?.(
            response.message || response.Message || "Lỗi khi cập nhật hạng mục",
            "error",
          );
        }
      } catch (error) {
        console.error("Error inline editing contract item:", error);
        onSnackbar?.("Lỗi khi cập nhật hạng mục", "error");
      } finally {
        setInlineSaving(false);
        await forceReload();
      }
    },
    [
      contractId,
      forceReload,
      isInlineCellEditable,
      normalizeInlineEditValue,
      onSnackbar,
      validateInlineEditValue,
    ],
  );

  const getInlineEditTextFieldProps = useCallback(
    ({ cell, row, table }) => {
      const field = cell.column.id;
      const savesOnChange = field === "status" || field === "difficultyLevel";

      return {
        size: "small",
        variant: "standard",
        onChange: savesOnChange
          ? (event) => {
            handleInlineEdit({
              item: row.original,
              field,
              value: event.target.value,
            });
            window.setTimeout(() => table.setEditingCell(null), 0);
          }
          : undefined,
        onBlur: savesOnChange
          ? undefined
          : (event) => {
            handleInlineEdit({
              item: row.original,
              field,
              value: event.target.value,
            });
          },
        onKeyDown: (event) => {
          if (
            [
              "ArrowLeft",
              "ArrowRight",
              "ArrowUp",
              "ArrowDown",
              "Home",
              "End",
            ].includes(event.key)
          ) {
            event.stopPropagation();
            return;
          }

          if (event.key === "Escape") {
            event.stopPropagation();
            table.setEditingCell(null);
          }
        },
        inputProps: {
          style: {
            fontFamily: "Inter, sans-serif",
            fontSize: "0.65rem",
            textAlign:
              field === "contractQuantity" || field === "unitPrice"
                ? "right"
                : "left",
          },
        },
        SelectProps: {
          MenuProps: {
            PaperProps: {
              sx: {
                maxHeight: 280,
                "& .MuiMenuItem-root": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.75rem",
                },
              },
            },
          },
        },
      };
    },
    [handleInlineEdit],
  );

  // Handle add new root item
  const handleAddRootItem = async () => {
    const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_ITEM_CREATE);
    if (result.hasPermission) {
      setModalMode("create");
      setModalParentItem(null);
      setModalEditItem(null);
      setModalOpen(true);
    } else {
      setPermissionSnackbar({
        open: true,
        message: "Bạn không có quyền tạo hạng mục hợp đồng.",
        severity: "warning",
      });
    }
  };

  // Handle payment columns switch
  const handlePaymentColumnsChange = async (event) => {
    const isChecked = event.target.checked;

    // Nếu đang tắt switch thì không cần kiểm tra quyền
    if (!isChecked) {
      setShowPaymentColumns(false);
      setDetailPanelOpen(false);
      setSelectedDetailItemId(null);
      return;
    }

    // Nếu đang bật switch thì kiểm tra quyền
    try {
      const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_READ);
      if (result.hasPermission) {
        setShowPaymentColumns(true);
      } else {
        setPermissionSnackbar({
          open: true,
          message: "Bạn không có quyền xem thông tin thanh toán",
          severity: "warning",
        });
      }
    } catch (error) {
      console.error("Error checking permission:", error);
      setPermissionSnackbar({
        open: true,
        message: "Lỗi kiểm tra quyền truy cập",
        severity: "error",
      });
    }
  };

  const handleOpenAcceptanceDetail = useCallback(
    async (item) => {
      if (!item || item.isSummaryRow) return;

      try {
        const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_READ);
        if (!result.hasPermission) {
          setPermissionSnackbar({
            open: true,
            message: "Bạn không có quyền xem thông tin thanh toán",
            severity: "warning",
          });
          return;
        }

        setShowPaymentColumns(true);
        await Promise.all([loadPaymentProfiles(), loadContractPayments()]);
        handleSelectDetailItem(item, { requirePaymentColumns: false });
      } catch (error) {
        console.error("Error checking permission:", error);
        setPermissionSnackbar({
          open: true,
          message: "Lỗi kiểm tra quyền truy cập",
          severity: "error",
        });
      }
    },
    [handleSelectDetailItem, loadContractPayments, loadPaymentProfiles],
  );

  useEffect(() => {
    if (!showPaymentColumns) {
      setDetailPanelOpen(false);
      setSelectedDetailItemId(null);
    }
  }, [showPaymentColumns]);

  // Handle convert to parent item (clear quantity, price, amount)
  const handleConvertToParent = async (item) => {
    try {
      const response = await contractItemsApi.convertToParent(item.itemId);

      if (response.Success || response.success) {
        onSnackbar?.(
          "Đã chuyển thành hạng mục cha - Xóa thông tin thực thi thành công",
          "success",
        );
        forceReload(); // Use force reload to update data
        setCannotAddChildDialogOpen(false);
        setItemCannotAddChild(null);
      } else {
        onSnackbar?.(
          response.Message || response.message || "Lỗi khi cập nhật hạng mục",
          "error",
        );
      }
    } catch (error) {
      console.error("Error converting to parent:", error);
      onSnackbar?.("Lỗi khi cập nhật hạng mục", "error");
    }
  };

  // Handle modal success
  const handleModalSuccess = (message) => {
    onSnackbar?.(message, "success");
    forceReload(); // Use force reload to update data
    setModalOpen(false);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setModalMode("create");
    setModalParentItem(null);
    setModalEditItem(null);
  };

  // Convert hierarchical data to flat structure for MRT
  const flattenData = useCallback((items, level = 0) => {
    let result = [];
    items.forEach((item) => {
      result.push({
        ...item,
        level_: level,
        hasChildren: item.children && item.children.length > 0,
      });
      if (item.children && item.children.length > 0) {
        result = result.concat(flattenData(item.children, level + 1));
      }
    });
    return result;
  }, []);

  // Get status color
  const getStatusColor = (status) => {
    const statusLower = status ? status.toLowerCase() : "";
    const colors = {
      "chưa bắt đầu": "#9e9e9e",
      "đang thực hiện": "#4caf50",
      "hoàn thành": "#2196f3",
      "tạm dừng": "#ff9800",
      "hủy bỏ": "#f44336",
    };
    return colors[statusLower] || "#9e9e9e";
  };

  // Get progress color based on percentage
  const getProgressColor = (percentage) => {
    if (percentage >= 100) return "#4caf50";
    if (percentage >= 75) return "#8bc34a";
    if (percentage >= 50) return "#ffc107";
    if (percentage >= 25) return "#ff9800";
    return "#f44336";
  };

  const getStatusChipStyles = (status) => {
    const color = getStatusColor(status);
    return {
      color,
      borderColor: `${color}55`,
      backgroundColor: `${color}12`,
    };
  };

  // Calculate summary totals
  const calculateSummaryTotals = useMemo(() => {
    // Tính tổng thành tiền của các hạng mục gốc (level 0) và lấy danh sách STT
    const rootItemCodes = [];
    const totalBeforeTax = items.reduce((sum, item) => {
      if (item.contractAmount) {
        rootItemCodes.push(item.itemCode);
        return sum + item.contractAmount;
      }
      return sum;
    }, 0);

    // Thuế VAT động dựa trên hợp đồng
    const vatRate = (contract?.vat || 8) / 100; // Default 8% if not specified
    const vatAmount = totalBeforeTax * vatRate;

    // Tổng sau thuế
    const totalAfterTax = totalBeforeTax + vatAmount;

    // Tổng sau thuế làm tròn (làm tròn đến hàng đơn vị - cho bảng hạng mục)
    const totalAfterTaxRounded = Math.round(totalAfterTax);

    // Tổng sau thuế làm tròn (làm tròn đến hàng nghìn - cho thông tin tổng hợp)
    const totalAfterTaxRoundedToThousands =
      Math.round(totalAfterTax / 1000) * 1000;

    // Làm tròn các giá trị cho thông tin tổng hợp hợp đồng
    const totalBeforeTaxRounded = Math.round(totalBeforeTax);
    const vatAmountRounded = Math.round(vatAmount);
    const totalAfterTaxRoundedForSummary = Math.round(totalAfterTax);

    return {
      totalBeforeTax,
      vatAmount,
      totalAfterTax,
      totalAfterTaxRounded,
      totalAfterTaxRoundedToThousands,
      totalBeforeTaxRounded,
      vatAmountRounded,
      totalAfterTaxRoundedForSummary,
      rootItemCodes, // Thêm danh sách STT của các hạng mục gốc
    };
  }, [items, contract?.vat]);

  const handleExportExcel = useCallback(async () => {
    if (!items || items.length === 0) {
      onSnackbar?.("Không có dữ liệu hạng mục để xuất Excel", "warning");
      return;
    }

    try {
      const user = getUser();
      const exportContractPayments =
        contractPayments.length > 0
          ? contractPayments
          : await loadContractPayments();
      const exportPaymentProfiles =
        paymentProfiles.length > 0 ? paymentProfiles : await loadPaymentProfiles();

      await exportContractItemsExcel({
        items,
        contract,
        paymentProfiles: exportPaymentProfiles,
        contractPayments: exportContractPayments,
        summaryTotals: calculateSummaryTotals,
        exportedBy: user?.fullName || user?.userName || "",
      });
      onSnackbar?.("Xuất Excel thành công", "success");
    } catch (error) {
      console.error("Error exporting contract items Excel:", error);
      onSnackbar?.("Lỗi khi xuất Excel", "error");
    }
  }, [
    calculateSummaryTotals,
    contract,
    contractPayments,
    items,
    loadContractPayments,
    loadPaymentProfiles,
    onSnackbar,
    paymentProfiles,
  ]);

  const handleOpenImportDialog = useCallback(async () => {
    try {
      const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_ITEM_CREATE);
      if (!result.hasPermission) {
        setPermissionSnackbar({
          open: true,
          message: "Bạn không có quyền import hạng mục hợp đồng.",
          severity: "warning",
        });
        return;
      }

      setImportDialogOpen(true);
    } catch (error) {
      console.error("Error checking permission:", error);
      setPermissionSnackbar({
        open: true,
        message: "Lỗi kiểm tra quyền truy cập",
        severity: "error",
      });
    }
  }, []);

  const handleImportSuccess = useCallback(() => {
    forceReload();
  }, [forceReload]);

  // Define columns for MRT
  // Generate payment columns dynamically
  const generatePaymentColumns = useCallback(() => {
    if (!showPaymentColumns || paymentProfiles.length === 0) {
      return [];
    }

    const paymentCols = [];

    // Add columns for each payment profile
    paymentProfiles.forEach((profile, index) => {
      // Sub-column group for each payment profile
      // const profileTitle = `Nghiệm thu đợt ${index + 1} - ${dayjs(
      //   profile.paymentDate,
      // ).format("DD/MM/YYYY")}`;
      const profileTitle = `Nghiệm thu đợt ${index + 1} - ${formatDate(profile.paymentDate)}`;

      paymentCols.push({
        id: `payment_profile_${profile.paymentProfileId}`,
        header: profileTitle,
        columns: [
          {
            id: `quantity_${profile.paymentProfileId}`,
            header: "Khối lượng",
            size: 90,
            Cell: ({ row }) => {
              const item = row.original;

              // Không hiển thị tổng cho quantity vì không cùng đơn vị
              if (item.isSummaryRow) {
                return null;
              }

              // Only show for leaf items (not parent items)
              if (isParentItem(item)) return null;

              const payment = contractPayments.find(
                (p) =>
                  p.contractItemId === item.itemId &&
                  p.paymentProfileId === profile.paymentProfileId,
              );

              if (!payment) {
                return null;
              }

              return (
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                  >
                    {formatPaymentQuantity(payment.paymentQuantity || 0)}
                  </Typography>
                </Box>
              );
            },
          },
          {
            id: `amount_${profile.paymentProfileId}`,
            header: "Thành tiền",
            size: 130,
            Cell: ({ row }) => {
              const item = row.original;

              // Xử lý hàng tổng
              if (item.isSummaryRow) {
                const totalAmount = item[`amount_${profile.paymentProfileId}`];
                if (!totalAmount || totalAmount === 0) return null;

                const isAfterTax = item.summaryType === "afterTax";

                return (
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "0.8rem",
                      textAlign: "right",
                      fontWeight: 700,
                      color: isAfterTax ? "#4caf50" : "#1976d2",
                      backgroundColor: isAfterTax ? "#e8f5e8" : "#e3f2fd",
                      padding: "4px 8px",
                      borderRadius: 1,
                    }}
                  >
                    {/* {formatAmount(totalAmount)} */}
                    {formatAmount(totalAmount)}
                  </Typography>
                );
              }

              // Nếu là hạng mục cha, hiển thị tổng từ các hạng mục con
              if (isParentItem(item)) {
                const parentTotal = calculateParentPaymentTotal(
                  item,
                  profile.paymentProfileId,
                );

                if (parentTotal === 0) {
                  return null;
                }

                return (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      textAlign: "right",
                      fontSize: "0.75rem",
                      color: "#1976d2",
                    }}
                  >
                    {formatAmount(parentTotal)}
                  </Typography>
                );
              }

              const payment = contractPayments.find(
                (p) =>
                  p.contractItemId === item.itemId &&
                  p.paymentProfileId === profile.paymentProfileId,
              );

              if (!payment) {
                return null;
              }

              // Calculate payment amount
              const amount =
                payment.paymentAmount && payment.paymentAmount > 0
                  ? Math.round(payment.paymentAmount)
                  : Math.round(
                    (item.unitPrice || 0) * (payment.paymentQuantity || 0),
                  );

              return (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    textAlign: "right",
                    fontSize: "0.75rem",
                    color: "#1976d2",
                  }}
                >
                  {formatAmount(amount)}
                </Typography>
              );
            },
          },
        ],
      });
    });

    // Thêm cột lũy kế với nested columns (khối lượng và thành tiền)
    if (paymentProfiles.length > 0) {
      paymentCols.push({
        id: "cumulative_total",
        header: "Lũy kế nghiệm thu",
        columns: [
          {
            id: "cumulative_quantity",
            header: "Khối lượng",
            size: 90,
            Cell: ({ row }) => {
              const item = row.original;

              // Không hiển thị tổng cho quantity vì không cùng đơn vị
              if (item.isSummaryRow) {
                return null;
              }

              // Only show for leaf items (not parent items)
              if (isParentItem(item)) return null;

              const totalQuantity = contractPayments
                .filter((p) => p.contractItemId === item.itemId)
                .reduce(
                  (sum, p) => sum + parseFloat(p.paymentQuantity || 0),
                  0,
                );

              if (totalQuantity === 0) {
                return null;
              }

              return (
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "#4caf50",
                    }}
                  >
                    {formatPaymentQuantity(totalQuantity)}
                  </Typography>
                </Box>
              );
            },
          },
          {
            id: "cumulative_amount",
            header: "Thành tiền",
            size: 140,
            Cell: ({ row }) => {
              const item = row.original;

              // Xử lý hàng tổng
              if (item.isSummaryRow) {
                const cumulativeAmount = item.cumulative_amount;
                if (!cumulativeAmount || cumulativeAmount === 0) return null;

                const isAfterTax = item.summaryType === "afterTax";

                return (
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "0.8rem",
                      textAlign: "right",
                      fontWeight: 700,
                      color: isAfterTax ? "#4caf50" : "#1976d2",
                      backgroundColor: isAfterTax ? "#e8f5e8" : "#e3f2fd",
                      padding: "4px 8px",
                      borderRadius: 1,
                    }}
                  >
                    {formatAmount(cumulativeAmount)}
                  </Typography>
                );
              }

              // Nếu là hạng mục cha, hiển thị tổng lũy kế từ các hạng mục con
              if (isParentItem(item)) {
                const parentCumulativeTotal = calculateParentPaymentTotal(item);

                if (parentCumulativeTotal === 0) {
                  return null;
                }

                return (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      textAlign: "right",
                      fontSize: "0.75rem",
                      color: "#4caf50",
                    }}
                  >
                    {formatAmount(parentCumulativeTotal)}
                  </Typography>
                );
              }

              // Tính tổng thành tiền lũy kế cho hạng mục lá
              const totalAmount = contractPayments
                .filter((p) => p.contractItemId === item.itemId)
                .reduce((sum, p) => {
                  const amount =
                    p.paymentAmount && p.paymentAmount > 0
                      ? Math.round(p.paymentAmount)
                      : Math.round(
                        (item.unitPrice || 0) * (p.paymentQuantity || 0),
                      );
                  return sum + amount;
                }, 0);

              if (totalAmount === 0) {
                return null;
              }

              return (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    textAlign: "right",
                    fontSize: "0.75rem",
                    color: "#4caf50",
                  }}
                >
                  {formatAmount(totalAmount)}
                </Typography>
              );
            },
          },
        ],
      });
    }

    return paymentCols;
  }, [
    showPaymentColumns,
    paymentProfiles,
    contractPayments,
    isParentItem,
    calculateParentPaymentTotal,
  ]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: "itemCode",
        header: "STT",
        size: 50,
        // minSize: 100,
        maxSize: 50,
        enableColumnPinning: true,
        enableHiding: false,
        Cell: ({ cell, row }) => {
          const item = row.original;
          const level = item.level_ || 0;

          // Xử lý hàng tổng
          if (item.isSummaryRow) {
            return (
              <Box display="flex" alignItems="center">
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: "#1976d2",
                    textTransform: "uppercase",
                  }}
                >
                  {cell.getValue()}
                </Typography>
              </Box>
            );
          }

          return (
            <Box display="flex" alignItems="center">
              <Box
                sx={{
                  width: 24,
                  height: 32,
                  // mr: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.hasChildren ? (
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleToggleExpand(item.itemId);
                    }}
                  // sx={{ p: 0.5 }}
                  >
                    {expandedItems.has(item.itemId) ? (
                      <ExpandMore sx={{ fontSize: "0.9rem" }} />
                    ) : (
                      <ChevronRight sx={{ fontSize: "0.9rem" }} />
                    )}
                  </IconButton>
                ) : null}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.65rem",
                  fontWeight: level === 0 ? 600 : 400,
                }}
              >
                {cell.getValue()}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "itemName",
        header: "Hạng mục công việc",
        size: 300,
        minSize: 170,
        maxSize: 350,
        enableColumnPinning: true,
        enableHiding: false,
        enableEditing: (row) => isInlineCellEditable(row.original, "itemName"),
        muiEditTextFieldProps: getInlineEditTextFieldProps,
        Cell: ({ cell, row }) => {
          const level = row.original.level_ || 0;
          const item = row.original;

          // Xử lý hàng tổng
          if (item.isSummaryRow) {
            return (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "#1976d2",
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                }}
              >
                {cell.getValue() || ""}
              </Typography>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.75,
                pl: `${Math.min(level, 4) * 14}px`,
                minWidth: 0,
              }}
            >
              {!item.hasChildren && (
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    mt: "7px",
                    flex: "0 0 auto",
                    backgroundColor: "#94a3b8",
                  }}
                />
              )}
              <Typography
                variant="body2"
                align="justify"
                sx={{
                  // alignContent: "left",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.65rem",
                  // fontWeight: item.hasChildren || level === 0 ? 650 : 400,
                  fontWeight: level === 0 ? 650 : 400,
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  lineHeight: 1.35,
                  color: item.hasChildren ? "#111827" : "#334155",
                }}
              >
                {cell.getValue() || ""}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "unit",
        header: "ĐVT",
        size: 45,
        enableEditing: (row) => isInlineCellEditable(row.original, "unit"),
        editVariant: "select",
        editSelectOptions: UNIT_OPTIONS.map((value) => ({
          value,
          label: value.toString(),
        })),
        muiEditTextFieldProps: getInlineEditTextFieldProps,
        Cell: ({ cell, row }) => {
          return (
            <Typography
              variant="body2"
              sx={{ fontFamily: "Inter, sans-serif", fontSize: "0.65rem" }}
            >
              {cell.getValue() || ""}
            </Typography>
          );
        },
      },
      {
        accessorKey: "difficultyLevel",
        header: "Khó khăn",
        size: 45,
        enableEditing: (row) =>
          isInlineCellEditable(row.original, "difficultyLevel"),
        editVariant: "select",
        editSelectOptions: DIFFICULTY_OPTIONS.map((value) => ({
          value,
          label: value.toString(),
        })),
        muiEditTextFieldProps: getInlineEditTextFieldProps,
        Cell: ({ cell, row }) => {
          const item = row.original;
          // Nếu là hạng mục cha (có children), không hiển thị
          if (isParentItem(item)) {
            return null;
          }

          const level = cell.getValue();

          // Nếu không có dữ liệu thực thi thực sự, ẩn các trường mặc định
          const hasExecutionData =
            item.contractQuantity && item.unitPrice && item.contractAmount;

          // Ẩn difficultyLevel nếu:
          // 1. Không có dữ liệu thực thi và giá trị = 1 (mặc định)
          // 2. Giá trị null/undefined/0
          if (
            level === null ||
            level === undefined ||
            level === 0 ||
            (!hasExecutionData && level === 1)
          ) {
            return (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.45rem",
                  color: "text.secondary",
                }}
              ></Typography>
            );
          }

          const getDifficultyLabel = (level) => {
            return level.toString();
          };

          const getDifficultyColor = (level) => {
            switch (level) {
              case 1:
                return "#4caf50"; // Xanh lá
              case 2:
                return "#8bc34a"; // Xanh lá nhạt
              case 3:
                return "#ff9800"; // Cam
              case 4:
                return "#f44336"; // Đỏ
              case 5:
                return "#9c27b0"; // Tím
              default:
                return "#9e9e9e"; // Xám
            }
          };

          return (
            <Chip
              label={getDifficultyLabel(level)}
              size="small"
              variant="outlined"
              sx={{
                borderColor: getDifficultyColor(level),
                color: getDifficultyColor(level),
                fontFamily: "Inter, sans-serif",
                fontSize: "0.525rem",
                height: "20px",
                "& .MuiChip-label": {
                  padding: "0 6px",
                },
              }}
            />
          );
        },
      },
      {
        accessorKey: "contractQuantity",
        header: "Khối lượng",
        size: 50,
        enableEditing: (row) =>
          isInlineCellEditable(row.original, "contractQuantity"),
        muiEditTextFieldProps: getInlineEditTextFieldProps,
        Cell: ({ cell, row }) => {
          const item = row.original;
          // Nếu là hạng mục cha (có children), không hiển thị
          if (isParentItem(item)) {
            return null;
          }

          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.65rem",
                textAlign: "right",
              }}
            >
              {cell.getValue()
                ? new Intl.NumberFormat("vi-VN", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                }).format(cell.getValue())
                : ""}
            </Typography>
          );
        },
      },
      {
        accessorKey: "unitPrice",
        header: "Đơn giá",
        size: 60,
        enableEditing: (row) =>
          isInlineCellEditable(row.original, "unitPrice"),
        muiEditTextFieldProps: getInlineEditTextFieldProps,
        Cell: ({ cell, row }) => {
          const item = row.original;
          // Nếu là hạng mục cha (có children), không hiển thị
          if (isParentItem(item)) {
            return null;
          }

          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.65rem",
                textAlign: "right",
              }}
            >
              {cell.getValue()
                ? formatCurrency(cell.getValue()).replace(/\s*₫\s*$/, "")
                : ""}
            </Typography>
          );
        },
      },
      {
        accessorKey: "contractAmount",
        header: "Thành tiền",
        size: 90,
        Cell: ({ cell, row }) => {
          const level = row.original.level_ || 0;
          const item = row.original;

          // Xử lý hàng tổng
          if (item.isSummaryRow) {
            const isAfterTax = item.summaryType === "afterTax";

            return (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.7rem",
                  textAlign: "right",
                  fontWeight: 700,
                  color: isAfterTax ? "#4caf50" : "#1976d2",
                  backgroundColor: isAfterTax ? "#e8f5e8" : "#e3f2fd",
                  padding: "4px 8px",
                  borderRadius: 1,
                }}
              >
                {cell.getValue()
                  ? formatCurrency(cell.getValue()).replace(/\s*₫\s*$/, "")
                  : ""}
              </Typography>
            );
          }

          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.65rem",
                textAlign: "right",
                fontWeight: level === 0 ? 600 : 400,
                color: level === 0 ? "#D36135" : "inherit",
              }}
            >
              {cell.getValue()
                ? formatCurrency(cell.getValue()).replace(/\s*₫\s*$/, "")
                : ""}
            </Typography>
          );
        },
      },
      {
        accessorKey: "priority",
        header: "Độ ưu tiên",
        size: 60,
        enableHiding: true,
        Cell: ({ cell, row }) => {
          const item = row.original;
          // Nếu là hạng mục cha (có children), không hiển thị
          if (isParentItem(item)) {
            return null;
          }

          const priority = cell.getValue();

          // Nếu không có dữ liệu thực thi thực sự, ẩn các trường mặc định
          const hasExecutionData =
            item.contractQuantity && item.unitPrice && item.contractAmount;

          // Ẩn priority nếu:
          // 1. Không có dữ liệu thực thi và priority = 2 (mặc định)
          // 2. Giá trị null/undefined
          if (
            priority === null ||
            priority === undefined ||
            (!hasExecutionData && priority === 2)
          ) {
            return (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.65rem",
                  color: "text.secondary",
                }}
              ></Typography>
            );
          }

          const getPriorityLabel = (priority) => {
            switch (priority) {
              case 1:
                return "Cao";
              case 2:
                return "Trung bình";
              case 3:
                return "Thấp";
              default:
                return "Không xác định";
            }
          };

          const getPriorityColor = (priority) => {
            switch (priority) {
              case 1:
                return "#f44336"; // Đỏ - Cao
              case 2:
                return "#ff9800"; // Cam - Trung bình
              case 3:
                return "#4caf50"; // Xanh lá - Thấp
              default:
                return "#9e9e9e"; // Xám
            }
          };

          return (
            <Chip
              label={getPriorityLabel(priority)}
              size="small"
              variant="outlined"
              sx={{
                borderColor: getPriorityColor(priority),
                color: getPriorityColor(priority),
                // fontFamily: "Inter, sans-serif",
                // fontSize: "0.525rem",
                // height: "20px",
                // "& .MuiChip-label": {
                //   padding: "0 6px",
                // },
                "& .MuiChip-label": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.58rem",
                  fontWeight: 600,
                },
              }}
            />
          );
        },
      },
      {
        accessorKey: "status",
        header: "Trạng thái",
        size: 60,
        enableEditing: (row) => isInlineCellEditable(row.original, "status"),
        editVariant: "select",
        editSelectOptions: STATUS_OPTIONS,
        muiEditTextFieldProps: getInlineEditTextFieldProps,
        Cell: ({ cell, row }) => {
          const item = row.original;
          // Nếu là hạng mục cha (có children), không hiển thị
          if (isParentItem(item)) {
            return null;
          }

          const status = cell.getValue();

          // Kiểm tra nếu không có execution data thì ẩn status mặc định
          const hasExecutionData =
            item.contractQuantity && item.unitPrice && item.contractAmount;

          // Ẩn status nếu:
          // 1. Không có dữ liệu thực thi và status = "Chưa bắt đầu" (mặc định)
          // 2. Giá trị null/undefined/empty
          if (
            status === null ||
            status === undefined ||
            status === "" ||
            (typeof status === "string" && status.trim() === "") ||
            (!hasExecutionData && status === "Chưa bắt đầu")
          ) {
            return (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.65rem",
                  color: "text.secondary",
                }}
              ></Typography>
            );
          }

          return (
            <Chip
              label={status}
              size="small"
              variant="outlined"
              sx={{
                ...getStatusChipStyles(status),
                // fontFamily: "Inter, sans-serif",
                // fontSize: "0.48rem",
                // height: 22,
                // fontWeight: 600,
                "& .MuiChip-label": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.58rem",
                  fontWeight: 600,
                },
              }}
            />
          );
        },
      },
      {
        accessorKey: "startDate",
        header: "Ngày bắt đầu",
        size: 70,
        enableHiding: true,
        Cell: ({ cell, row }) => {
          const item = row.original;
          // Nếu là hạng mục cha (có children), không hiển thị
          if (isParentItem(item)) {
            return null;
          }

          return (
            <Typography
              variant="body2"
              sx={{ fontFamily: "Inter, sans-serif", fontSize: "0.65rem" }}
            >
              {/* {cell.getValue()
                ? new Date(cell.getValue()).toLocaleDateString("vi-VN")
                : ""} */}
              {cell.getValue()
                ? formatDate(cell.getValue())
                : ""}
            </Typography>
          );
        },
      },
      {
        accessorKey: "endDate",
        header: "Ngày kết thúc",
        size: 70,
        enableHiding: true,
        Cell: ({ cell, row }) => {
          const item = row.original;
          // Nếu là hạng mục cha (có children), không hiển thị
          if (isParentItem(item)) {
            return null;
          }

          return (
            <Typography
              variant="body2"
              sx={{ fontFamily: "Inter, sans-serif", fontSize: "0.65rem" }}
            >
              {/* {cell.getValue()
                ? new Date(cell.getValue()).toLocaleDateString("vi-VN")
                : ""} */}
              {cell.getValue()
                ? formatDate(cell.getValue())
                : ""}
            </Typography>
          );
        },
      },
      {
        accessorKey: "completionPercentage",
        header: "Tiến độ",
        size: 60,
        Cell: ({ cell, row }) => {
          const item = row.original;
          // Nếu là hạng mục cha (có children), không hiển thị
          if (isParentItem(item)) {
            return null;
          }

          const percentage = cell.getValue();

          // Kiểm tra nếu không có execution data thì ẩn progress mặc định
          const hasExecutionData =
            item.contractQuantity && item.unitPrice && item.contractAmount;

          // Ẩn completionPercentage nếu:
          // 1. Không có dữ liệu thực thi và percentage = 0 (mặc định)
          // 2. Giá trị null/undefined
          if (
            percentage === null ||
            percentage === undefined ||
            (!hasExecutionData && percentage === 0)
          ) {
            return (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.65rem",
                  color: "text.secondary",
                }}
              ></Typography>
            );
          }

          // Chuyển đổi thành số, nếu là 0 thì vẫn hiển thị
          const numPercentage = Number(percentage) || 0;

          return (
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <Box
                sx={{
                  width: "80px",
                  height: "8px",
                  backgroundColor: "#e0e0e0",
                  borderRadius: "4px",
                  mr: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${Math.min(numPercentage, 100)}%`,
                    height: "100%",
                    backgroundColor: getProgressColor(numPercentage),
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.525rem",
                  minWidth: "35px",
                  color: "text.secondary",
                }}
              >
                {Math.round(numPercentage)}%
              </Typography>
            </Box>
          );
        },
      },
    ];

    // const paymentIndicatorColumn = {
    //   accessorKey: "paymentIndicator",
    //   header: "Thanh toán",
    //   size: 90,
    //   enableSorting: false,
    //   enableHiding: false,
    //   Cell: ({ row }) => {
    //     const item = row.original;

    //     if (item.isSummaryRow) {
    //       return null;
    //     }

    //     if (!showPaymentColumns) {
    //       return (
    //         <Typography
    //           variant="caption"
    //           sx={{
    //             fontFamily: "Inter, sans-serif",
    //             fontSize: "0.58rem",
    //             color: "text.secondary",
    //           }}
    //         >
    //           Chưa tải
    //         </Typography>
    //       );
    //     }

    //     const collectLeafItems = (sourceItem) => {
    //       if (!sourceItem?.children || sourceItem.children.length === 0) {
    //         return [sourceItem];
    //       }
    //       return sourceItem.children.flatMap((child) =>
    //         collectLeafItems(child),
    //       );
    //     };
    //     const relatedPayments = isParentItem(item)
    //       ? collectLeafItems(item).flatMap((leaf) =>
    //         contractPayments.filter(
    //           (payment) => payment.contractItemId === leaf.itemId,
    //         ),
    //       )
    //       : contractPayments.filter(
    //         (payment) => payment.contractItemId === item.itemId,
    //       );
    //     const beforeTax = isParentItem(item)
    //       ? calculateParentPaymentTotal(item)
    //       : relatedPayments.reduce((total, payment) => {
    //         const amount =
    //           payment.paymentAmount && payment.paymentAmount > 0
    //             ? Math.round(payment.paymentAmount)
    //             : Math.round(
    //               (item.unitPrice || 0) * (payment.paymentQuantity || 0),
    //             );
    //         return total + amount;
    //       }, 0);
    //     const progress =
    //       item.contractAmount && item.contractAmount > 0
    //         ? Math.min((beforeTax / item.contractAmount) * 100, 100)
    //         : 0;

    //     if (relatedPayments.length === 0) {
    //       return (
    //         <Typography
    //           variant="caption"
    //           sx={{
    //             fontFamily: "Inter, sans-serif",
    //             fontSize: "0.58rem",
    //             color: "text.secondary",
    //           }}
    //         >
    //           Chưa nghiệm thu
    //         </Typography>
    //       );
    //     }

    //     return (
    //       <Box sx={{ minWidth: 92 }}>
    //         <Typography
    //           variant="caption"
    //           sx={{
    //             fontFamily: "Inter, sans-serif",
    //             fontSize: "0.58rem",
    //             fontWeight: 600,
    //             color: "#2563eb",
    //             lineHeight: 1.1,
    //             display: "block",
    //           }}
    //         >
    //           {relatedPayments.length} đợt
    //         </Typography>
    //         <Box
    //           sx={{
    //             mt: 0.25,
    //             height: 4,
    //             borderRadius: 1,
    //             backgroundColor: "#e5e7eb",
    //             overflow: "hidden",
    //           }}
    //         >
    //           <Box
    //             sx={{
    //               width: `${Math.min(progress, 100)}%`,
    //               height: "100%",
    //               backgroundColor: "#16a34a",
    //             }}
    //           />
    //         </Box>
    //       </Box>
    //     );
    //   },
    // };

    // Actions column
    const actionsColumn = {
      accessorKey: "actions",
      header: "",
      size: 60,
      enableColumnPinning: true,
      enableSorting: false,
      enableHiding: false,
      Cell: ({ row }) => {
        const item = row.original;

        // Không hiển thị menu cho hàng tổng
        if (item.isSummaryRow) {
          return null;
        }

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.25,
            }}
          >
            <Tooltip title="Thông tin nghiệm thu" arrow>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenAcceptanceDetail(item);
                }}
                sx={{ p: 0.5 }}
              >
                <Payment sx={{ fontSize: "0.8rem" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Chỉnh sửa" arrow>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleEdit(item);
                }}
                sx={{ p: 0.5 }}
              >
                <Edit sx={{ fontSize: "0.8rem" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Thao tác" arrow>
              <IconButton
                size="small"
                onClick={(event) => handleMenuOpen(event, item)}
                sx={{ p: 0.5 }}
              >
                <MoreVert sx={{ fontSize: "0.8rem" }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    };

    const mainColumns = baseColumns.filter(
      (column) =>
        !["priority", "startDate", "endDate"].includes(column.accessorKey),
      // !["startDate", "endDate"].includes(column.accessorKey),
    );

    // return [...mainColumns, paymentIndicatorColumn, actionsColumn];
    return [...mainColumns, actionsColumn];
  }, [
    expandedItems,
    getInlineEditTextFieldProps,
    handleEdit,
    handleOpenAcceptanceDetail,
    handleToggleExpand,
    handleMenuOpen,
    isInlineCellEditable,
    isParentItem,
    getStatusChipStyles,
    showPaymentColumns,
    contractPayments,
    calculateParentPaymentTotal,
  ]);

  // Filter data based on expanded state for hierarchical display
  const visibleData = useMemo(() => {
    const result = [];
    const addVisibleItems = (items, level = 0) => {
      items.forEach((item) => {
        result.push({ ...item, level_: level });
        if (
          item.children &&
          item.children.length > 0 &&
          expandedItems.has(item.itemId)
        ) {
          addVisibleItems(item.children, level + 1);
        }
      });
    };
    addVisibleItems(items);

    // Thêm hàng tổng vào cuối
    if (items.length > 0) {
      // Tính tổng các cột thanh toán từ paymentProfiles
      const paymentTotals = {};
      const cumulativeTotals = {};

      if (showPaymentColumns && paymentProfiles.length > 0) {
        paymentProfiles.forEach((profile) => {
          // Tính tổng amount cho từng profile (bỏ quantity vì không cùng đơn vị)
          const profilePayments = contractPayments.filter(
            (p) => p.paymentProfileId === profile.paymentProfileId,
          );

          const totalAmount = profilePayments.reduce((sum, p) => {
            // Tìm contract item để lấy unit price
            const contractItem = items
              .flatMap((item) => [item, ...(item.children || [])])
              .find((item) => item.itemId === p.contractItemId);

            const amount =
              p.paymentAmount && p.paymentAmount > 0
                ? p.paymentAmount
                : (contractItem?.unitPrice || 0) * (p.paymentQuantity || 0);

            return sum + amount;
          }, 0);

          // Chỉ lưu amount, bỏ quantity
          paymentTotals[`payment_amount_${profile.paymentProfileId}`] =
            totalAmount;
        });

        // Tính tổng lũy kế
        let cumulativeAmount = 0;
        paymentProfiles.forEach((profile) => {
          cumulativeAmount +=
            paymentTotals[`payment_amount_${profile.paymentProfileId}`] || 0;
          cumulativeTotals[`cumulative_${profile.paymentProfileId}`] =
            cumulativeAmount;
        });

        // Tính tổng cho các cột nghiệm thu gốc (quantity_X và amount_X)
        paymentProfiles.forEach((profile) => {
          const profilePayments = contractPayments.filter(
            (p) => p.paymentProfileId === profile.paymentProfileId,
          );

          // Chỉ tính tổng thành tiền nghiệm thu (bỏ quantity vì không cùng đơn vị)
          const totalProfileAmount = profilePayments.reduce((sum, p) => {
            const contractItem = items
              .flatMap((item) => [item, ...(item.children || [])])
              .find((item) => item.itemId === p.contractItemId);

            const amount =
              p.paymentAmount && p.paymentAmount > 0
                ? p.paymentAmount
                : (contractItem?.unitPrice || 0) * (p.paymentQuantity || 0);

            return sum + amount;
          }, 0);

          // Chỉ lưu amount
          paymentTotals[`amount_${profile.paymentProfileId}`] =
            totalProfileAmount;
        });

        // Tính tổng cho các cột lũy kế nghiệm thu (chỉ amount, bỏ quantity vì không cùng đơn vị)
        let totalCumulativeAmount = 0;

        contractPayments.forEach((payment) => {
          const contractItem = items
            .flatMap((item) => [item, ...(item.children || [])])
            .find((item) => item.itemId === payment.contractItemId);

          const amount =
            payment.paymentAmount && payment.paymentAmount > 0
              ? payment.paymentAmount
              : (contractItem?.unitPrice || 0) * (payment.paymentQuantity || 0);

          totalCumulativeAmount += amount;
        });

        // Chỉ lưu cumulative_amount
        paymentTotals["cumulative_amount"] = totalCumulativeAmount;
      }

      // Hàng tổng trước thuế
      const summaryRowBeforeTax = {
        itemId: "SUMMARY_ROW_BEFORE_TAX",
        itemCode: "TỔNG",
        itemName: "(trước thuế)",
        quantity: null, // Không tính tổng quantity vì không cùng đơn vị
        unitPrice: null, // Không hiển thị đơn giá cho hàng tổng
        contractAmount: calculateSummaryTotals.totalBeforeTax,
        level_: -1, // Đánh dấu đây là hàng tổng
        isSummaryRow: true,
        summaryType: "beforeTax",
        ...paymentTotals,
        ...cumulativeTotals,
      };

      result.push(summaryRowBeforeTax);

      // Tính toán cho hàng tổng sau thuế
      const afterTaxPaymentTotals = {};
      const afterTaxCumulativeTotals = {};

      // Tính tổng sau thuế cho từng profile payment
      if (showPaymentColumns && paymentProfiles.length > 0) {
        paymentProfiles.forEach((profile) => {
          const beforeTaxAmount =
            paymentTotals[`amount_${profile.paymentProfileId}`] || 0;
          // Lấy VAT của profile, nếu không có thì dùng VAT hợp đồng
          const vatRate = (profile.vat || contract?.vat || 8) / 100;
          const afterTaxAmount = beforeTaxAmount * (1 + vatRate);

          afterTaxPaymentTotals[`amount_${profile.paymentProfileId}`] =
            Math.round(afterTaxAmount);
        });

        // Tính tổng lũy kế sau thuế (cộng các tổng sau thuế của các đợt)
        let totalAfterTaxCumulative = 0;
        paymentProfiles.forEach((profile) => {
          totalAfterTaxCumulative +=
            afterTaxPaymentTotals[`amount_${profile.paymentProfileId}`] || 0;
        });

        afterTaxCumulativeTotals["cumulative_amount"] = Math.round(
          totalAfterTaxCumulative,
        );
      }

      // Hàng tổng sau thuế
      const summaryRowAfterTax = {
        itemId: "SUMMARY_ROW_AFTER_TAX",
        itemCode: "TỔNG",
        itemName: "(sau thuế)",
        quantity: null,
        unitPrice: null,
        contractAmount: calculateSummaryTotals.totalAfterTax, // Sử dụng tổng sau thuế từ calculateSummaryTotals
        level_: -1,
        isSummaryRow: true,
        summaryType: "afterTax",
        ...afterTaxPaymentTotals,
        ...afterTaxCumulativeTotals,
      };

      result.push(summaryRowAfterTax);
    }

    return result;
  }, [
    items,
    expandedItems,
    showPaymentColumns,
    paymentProfiles,
    contractPayments,
    calculateSummaryTotals.totalBeforeTax,
    calculateSummaryTotals.totalAfterTax,
    contract?.vat,
  ]);

  const allItemsFlat = useMemo(() => flattenData(items), [flattenData, items]);

  const selectedDetailItem = useMemo(() => {
    return allItemsFlat.find((item) => item.itemId === selectedDetailItemId);
  }, [allItemsFlat, selectedDetailItemId]);

  const getLeafDescendants = useCallback((item) => {
    const collect = (sourceItem) => {
      if (!sourceItem?.children || sourceItem.children.length === 0) {
        return [sourceItem];
      }
      return sourceItem.children.flatMap((child) => collect(child));
    };

    return collect(item);
  }, []);

  const getItemPaymentSummary = useCallback(
    (item) => {
      if (!item) {
        return {
          count: 0,
          beforeTax: 0,
          afterTax: 0,
          progress: 0,
        };
      }

      const beforeTax = isParentItem(item)
        ? calculateParentPaymentTotal(item)
        : contractPayments
          .filter((payment) => payment.contractItemId === item.itemId)
          .reduce((total, payment) => {
            const amount =
              payment.paymentAmount && payment.paymentAmount > 0
                ? Math.round(payment.paymentAmount)
                : Math.round(
                  (item.unitPrice || 0) * (payment.paymentQuantity || 0),
                );
            return total + amount;
          }, 0);

      const relatedPayments = isParentItem(item)
        ? getLeafDescendants(item).flatMap((leaf) =>
          contractPayments.filter(
            (payment) => payment.contractItemId === leaf.itemId,
          ),
        )
        : contractPayments.filter(
          (payment) => payment.contractItemId === item.itemId,
        );

      const afterTax = relatedPayments.reduce((total, payment) => {
        const profile = paymentProfiles.find(
          (item) => item.paymentProfileId === payment.paymentProfileId,
        );
        const sourceItem = isParentItem(item)
          ? getLeafDescendants(item).find(
            (leaf) => leaf.itemId === payment.contractItemId,
          )
          : item;
        const amount =
          payment.paymentAmount && payment.paymentAmount > 0
            ? Math.round(payment.paymentAmount)
            : Math.round(
              (sourceItem?.unitPrice || 0) * (payment.paymentQuantity || 0),
            );
        const vatRate = (profile?.vat || contract?.vat || 8) / 100;
        return total + Math.round(amount * (1 + vatRate));
      }, 0);

      const progress =
        item.contractAmount && item.contractAmount > 0
          ? Math.min((beforeTax / item.contractAmount) * 100, 100)
          : 0;

      return {
        count: relatedPayments.length,
        beforeTax,
        afterTax,
        progress,
      };
    },
    [
      calculateParentPaymentTotal,
      contract?.vat,
      contractPayments,
      getLeafDescendants,
      isParentItem,
      paymentProfiles,
    ],
  );

  const renderCompactField = (label, value, options = {}) => (
    <Box className="contract-detail-field">
      <Typography className="contract-detail-label">{label}</Typography>
      <Typography
        className={`contract-detail-value ${options.strong ? "contract-detail-value-strong" : ""
          } ${options.money ? "contract-detail-value-money" : ""}`}
      >
        {value === null || value === undefined || value === "" ? "-" : value}
      </Typography>
    </Box>
  );

  const renderDetailSection = (title, children, className = "") => (
    <Box className={`contract-detail-section ${className}`}>
      <Typography className="contract-detail-section-title">{title}</Typography>
      {children}
    </Box>
  );

  const renderDetailPanelContent = () => {
    if (!selectedDetailItem) {
      return (
        <Box className="contract-detail-empty">
          <InfoOutlined sx={{ fontSize: "1.2rem", color: "#64748b" }} />
          <Typography className="contract-detail-empty-title">
            Chọn một hạng mục
          </Typography>
          <Typography className="contract-detail-empty-text">
            Chọn một dòng trong cây hạng mục để xem tổng quan, thực thi và thanh toán.
          </Typography>
        </Box>
      );
    }

    const isParent = isParentItem(selectedDetailItem);
    const paymentSummary = getItemPaymentSummary(selectedDetailItem);
    const relatedLeafItems = isParent
      ? getLeafDescendants(selectedDetailItem)
      : [selectedDetailItem];
    const paymentCards = paymentProfiles
      .map((profile, index) => {
        const profilePayments = relatedLeafItems.flatMap((leaf) =>
          contractPayments
            .filter(
              (payment) =>
                payment.contractItemId === leaf.itemId &&
                payment.paymentProfileId === profile.paymentProfileId,
            )
            .map((payment) => ({ payment, leaf })),
        );
        const beforeTaxAmount = profilePayments.reduce(
          (total, { payment, leaf }) => {
            const amount =
              payment.paymentAmount && payment.paymentAmount > 0
                ? Math.round(payment.paymentAmount)
                : Math.round((leaf.unitPrice || 0) * (payment.paymentQuantity || 0));
            return total + amount;
          },
          0,
        );
        const totalQuantity = profilePayments.reduce(
          (total, { payment }) => total + (Number(payment.paymentQuantity) || 0),
          0,
        );
        const vatRate = (profile.vat || contract?.vat || 8) / 100;
        const afterTaxAmount = Math.round(beforeTaxAmount * (1 + vatRate));

        if (profilePayments.length === 0 && beforeTaxAmount === 0) {
          return null;
        }

        return (
          <Box key={profile.paymentProfileId} className="contract-payment-card">
            <Box className="contract-payment-card-header">
              <Box>
                <Typography className="contract-payment-phase">
                  Đợt nghiệm thu {index + 1}
                </Typography>
                <Typography className="contract-payment-subtext">
                  {/* {profile.paymentDate
                    ? new Date(profile.paymentDate).toLocaleDateString("vi-VN")
                    : "-"}{" "}
                  · VAT {profile.vat || contract?.vat || 8}% */}
                  {profile.paymentDate
                    ? formatDate(profile.paymentDate)
                    : "-"}{" "}
                  - VAT {profile.vat || contract?.vat || 8}%
                </Typography>
              </Box>
              {/* <Chip
                label={`${profilePayments.length} dòng`}
                size="small"
                className="contract-payment-count-chip"
              /> */}
            </Box>
            <Box className="contract-payment-card-grid">
              {renderCompactField("Trước thuế", formatAmount(beforeTaxAmount), {
                money: true,
              })}
              {renderCompactField(
                "Khối lượng",
                totalQuantity ? formatPaymentQuantity(totalQuantity) : "",
              )}
              {renderCompactField("Sau thuế", formatAmount(afterTaxAmount), {
                money: true,
              })}
            </Box>
          </Box>
        );
      })
      .filter(Boolean);

    return (
      <Box className="contract-detail-panel-inner">
        <Box className="contract-detail-header">
          <Box sx={{ minWidth: 0 }}>
            <Box className="contract-detail-header-meta">
              <Typography className="contract-detail-code">
                {selectedDetailItem.itemCode || "-"}
              </Typography>
              {/* <Chip
                label={isParent ? "Hạng mục cha" : "Hạng mục thực thi"}
                size="small"
                className={isParent ? "contract-type-chip-parent" : "contract-type-chip-leaf"}
              /> */}
            </Box>
            <Typography className="contract-detail-name">
              {selectedDetailItem.itemName || "-"}
            </Typography>
          </Box>
          <Tooltip title="Đóng">
            <IconButton size="small" onClick={handleCloseDetailPanel}>
              <Close sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box className="contract-detail-body">
          {renderDetailSection(
            "Thanh toán / Nghiệm thu",
            <Stack spacing={1}>
              <Box className="contract-detail-metrics">
                {/* {renderCompactField("Số dòng nghiệm thu", paymentSummary.count)} */}
                {renderCompactField("Lũy kế trước thuế", formatAmount(paymentSummary.beforeTax), {
                  strong: true,
                  money: true,
                })}
                {renderCompactField("Tiến độ thanh toán", `${Math.round(paymentSummary.progress)}%`)}
                {renderCompactField("Lũy kế sau thuế", formatAmount(paymentSummary.afterTax), {
                  strong: true,
                  money: true,
                })}
              </Box>
              {showPaymentColumns && paymentCards.length === 0 && (
                <Box className="contract-detail-empty-inline">
                  Chưa có dữ liệu nghiệm thu cho hạng mục này.
                </Box>
              )}
              {paymentCards}
            </Stack>,
          )}
        </Box>
      </Box>
    );
  };
  const renderDetailPanel = () => (
    <Box className="contract-detail-panel">{renderDetailPanelContent()}</Box>
  );

  const contractItemsTableConfig = useMemo(
    () => ({
      ...tableConfig,
      columns,
      data: visibleData,
      defaultColumn: {
        ...tableConfig.defaultColumn,
        enableEditing: false,
      },
      enableEditing: true,
      editDisplayMode: "cell",
      enableRowActions: false,
      enableColumnPinning: true,
      // enableFilters: false,
      enableColumnFilters: false,
      enableBottomToolbar: false,
      enablePagination: false,
      enableSorting: false,
      state: {
        ...tableConfig.state,
        showProgressBars: inlineSaving,
      },
      initialState: {
        ...tableConfig.initialState,
        columnPinning: {
          left: ["itemCode", "itemName"],
          right: ["actions"],
        },
        density: "compact",
      },
      muiTableHeadCellProps: {
        ...tableConfig.muiTableHeadCellProps,
        sx: {
          ...tableConfig.muiTableHeadCellProps?.sx,
          fontSize: "0.65rem",

          // "& .MuiTypography-root": {
          //   fontSize: "0.65rem",
          // },

          // "& .MuiChip-label": {
          //   fontSize: "0.5rem",
          // },
        },
      },
      renderTopToolbarCustomActions: () => (
        <Box className="contract-items-toolbar">
          <Tooltip title="Mở rộng tất cả">
            <IconButton size="small" onClick={handleExpandAll}>
              <UnfoldMore sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Thu gọn tất cả">
            <IconButton size="small" onClick={handleCollapseAll}>
              <UnfoldLess sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownload sx={{ fontSize: "1rem" }} />}
            onClick={handleExportExcel}
            sx={{
              textTransform: "none",
              fontFamily: "Inter, sans-serif",
              fontSize: "0.75rem",
              minHeight: 30,
            }}
          >
            Xuất Excel
          </Button>
          {/* <FormControlLabel
            control={
              <Switch
                checked={showPaymentColumns}
                onChange={handlePaymentColumnsChange}
                size="small"
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Payment sx={{ fontSize: "1rem", color: "#1976d2" }} />
                <Typography variant="body2">Hiển thị nghiệm thu</Typography>
              </Box>
            }
            sx={{ mr: 2 }}
          />
          <Typography variant="caption" color="text.secondary">
            {showPaymentColumns
              ? "Chọn dòng để xem chi tiết nghiệm thu"
              : "Bật Hiển thị nghiệm thu để xem chi tiết nghiệm thu"}
          </Typography> */}
        </Box>
      ),
      muiTableContainerProps: {
        ...tableConfig.muiTableContainerProps,
        sx: {
          ...tableConfig.muiTableContainerProps?.sx,
          maxHeight: "calc(100vh - 285px)",
          minWidth: "100%",
          "& .MuiTable-root": {
            minWidth: "max-content",
          },
        },
      },
      muiTableBodyCellProps: ({ cell, row }) => {
        const field = cell.column.id;
        const canInlineEdit = isInlineCellEditable(row.original, field);

        return {
          ...tableConfig.muiTableBodyCellProps,
          title: canInlineEdit ? "Double click để chỉnh sửa nhanh" : undefined,
          onClick: canInlineEdit
            ? (event) => {
              event.stopPropagation();
            }
            : undefined,
          onDoubleClick: canInlineEdit
            ? (event) => {
              event.stopPropagation();
            }
            : undefined,
          sx: {
            ...tableConfig.muiTableBodyCellProps?.sx,
            cursor: canInlineEdit ? "cell" : undefined,
            "&:hover": canInlineEdit
              ? {
                backgroundColor: "#fff7ed !important",
                outline: "1px solid #fb923c",
                outlineOffset: "-1px",
              }
              : undefined,
          },
        };
      },
      muiTableBodyRowProps: ({ row }) => {
        const item = row.original;

        // Styling đặc biệt cho hàng tổng
        if (item.isSummaryRow) {
          const isAfterTax = item.summaryType === "afterTax";
          const borderColor = isAfterTax ? "#4caf50" : "#1976d2";
          const backgroundColor = isAfterTax ? "#f0f8f0" : "#f0f7ff";
          const hoverColor = isAfterTax ? "#e6f3e6" : "#e6f1ff";

          return {
            sx: {
              backgroundColor,
              borderTop: `2px solid ${borderColor}`,
              "&:hover": {
                backgroundColor: `${hoverColor} !important`,
              },
              "& .MuiTableCell-root": {
                borderTop: `2px solid ${borderColor}`,
                fontWeight: 700,
              },
            },
          };
        }

        // Styling thông thường
        const isSelected = selectedDetailItemId === item.itemId;
        return {
          // Không mở thông tin nghiệm thu khi click trực tiếp vào dòng.
          // Dùng nút action "Nghiệm thu" trong cột hành động để mở panel.
          // onClick: showPaymentColumns
          //   ? () => handleSelectDetailItem(item)
          //   : undefined,
          sx: {
            cursor: "default",
            backgroundColor: isSelected
              ? "#e8f1ff"
              : item.level_ === 0
                ? "rgba(0, 0, 0, 0.035)"
                : "#ffffff",
            "&:hover": {
              backgroundColor: isSelected
                ? "#dbeafe"
                : item.level_ === 0
                  ? "rgba(0, 0, 0, 0.06)"
                  : "rgba(0, 0, 0, 0.04)",
            },
            "& .MuiTableCell-root": {
              backgroundColor: isSelected
                ? "#eff6ff !important"
                : item.level_ === 0
                  ? "#f8fafc !important"
                  : "#ffffff !important",
            },
            "& .MuiTableCell-root:first-of-type": {
              borderLeft: isSelected
                ? "3px solid #2563eb"
                : item.level_ >= 2
                  ? "3px solid #dbe4ef"
                  : "3px solid transparent",
            },
          },
        };
      },
    }),
    [
      columns,
      handleCollapseAll,
      handleExpandAll,
      handleExportExcel,
      handlePaymentColumnsChange,
      handleSelectDetailItem,
      inlineSaving,
      isInlineCellEditable,
      selectedDetailItemId,
      showPaymentColumns,
      tableConfig,
      visibleData,
    ],
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress size={24} sx={{ mr: 1 }} />
        <Typography variant="body2" sx={{ fontFamily: "Inter, sans-serif" }}>
          Đang tải dữ liệu...
        </Typography>
      </Box>
    );
  }

  const showImportExcelButton = items.length === 0;

  if (items.length === 0) {
    return (
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: "1rem",
            }}
          >
            Hạng mục hợp đồng
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AppButton
              variant="contained"
              startIcon={<Add />}
              size="small"
              onClick={handleAddRootItem}
            >
              Thêm hạng mục
            </AppButton>
            <AppButton
              disabled={!showImportExcelButton}
              variant="outlined"
              size="small"
              startIcon={<UploadFile sx={{ fontSize: "1rem" }} />}
              onClick={handleOpenImportDialog}
            >
              Import Excel
            </AppButton>
          </Box>
        </Box>
        <Alert
          severity="info"
          sx={{
            fontFamily: "Inter, sans-serif",
            fontSize: "0.875rem",
          }}
        >
          Chưa có hạng mục nào được tạo cho hợp đồng này. Nhấn "Thêm hạng mục"
          để bắt đầu.
        </Alert>

        {/* Contract Item Modal - Always render even when no items */}
        <ContractItemModal
          open={modalOpen}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          contractId={contractId}
          editMode={modalMode === "edit"}
          itemData={modalEditItem}
          parentItem={modalParentItem}
        />

        <ContractItemsImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          contractId={contractId}
          existingItems={items}
          onImportSuccess={handleImportSuccess}
          onSnackbar={onSnackbar}
        />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: "1rem",
            }}
          >
            Hạng mục hợp đồng ({items.length} hạng mục gốc)
          </Typography>
          {/* <Typography
            variant="caption"
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.72rem",
              color: "text.secondary",
            }}
          >
            💡 Cuộn ngang để xem thêm thông tin
          </Typography> */}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AppButton
            variant="contained"
            startIcon={<Add />}
            size="small"
            onClick={handleAddRootItem}
          >
            Thêm hạng mục
          </AppButton>
          <AppButton
            disabled={!showImportExcelButton}
            variant="outlined"
            size="small"
            startIcon={<UploadFile sx={{ fontSize: "1rem" }} />}
            onClick={handleOpenImportDialog}
          >
            Import Excel
          </AppButton>
        </Box>
      </Box>

      <Box
        className={`contract-items-workspace ${detailPanelOpen && selectedDetailItem ? "detail-open" : ""
          }`}
      >
        <Box className="contract-items-master">
          {/* Material React Table */}
          <Box className="contract-items-table-shell">
            <MaterialReactTable {...contractItemsTableConfig} />
          </Box>

          {/* Summary Totals Section */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: "0.875rem",
                mb: 2,
                color: "#1976d2",
              }}
            >
              📊 Thông tin tổng hợp hợp đồng
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 1,
                maxWidth: 500,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 0.5,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  Tổng giá trước thuế (
                  {calculateSummaryTotals.rootItemCodes.length > 0
                    ? calculateSummaryTotals.rootItemCodes.join("+")
                    : "Chưa có hạng mục"}
                  )
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "right",
                  }}
                >
                  {formatAmount(
                    calculateSummaryTotals.totalBeforeTaxRounded,
                  )}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 0.5,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  Thuế VAT ({contract?.vat || 8}%)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "right",
                  }}
                >
                  {formatAmount(calculateSummaryTotals.vatAmountRounded)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 0.5,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#1976d2",
                  }}
                >
                  Tổng giá sau thuế
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "right",
                    color: "#1976d2",
                  }}
                >
                  {formatAmount(
                    calculateSummaryTotals.totalAfterTaxRoundedForSummary,
                  )}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 0.5,
                  backgroundColor: "#fff3e0",
                  // px: 1,
                  // borderRadius: 0.5,
                  // border: "1px solid #ffcc80",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#f57c00",
                  }}
                >
                  Tổng giá làm tròn
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "right",
                    color: "#f57c00",
                  }}
                >
                  {formatAmount(
                    calculateSummaryTotals.totalAfterTaxRoundedToThousands,
                  )}
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="caption"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.6rem",
                color: "text.secondary",
                mt: 1,
                display: "block",
              }}
            >
              💡 Các giá trị được tự động tính toán dựa trên tổng thành tiền của
              các hạng mục gốc
            </Typography>
          </Box>

          {/* Payment Summary Section */}
          {showPaymentColumns && paymentProfiles.length > 0 && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                backgroundColor: "#f9f9f9",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  mb: 2,
                  color: "#1976d2",
                }}
              >
                💰 Thông tin tổng hợp thanh toán
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 1,
                  maxWidth: 800,
                }}
              >
                {/* Từng đợt thanh toán */}
                {paymentProfiles.map((profile, index) => {
                  const beforeTaxAmount =
                    visibleData.find(
                      (item) => item.summaryType === "beforeTax",
                    )?.[`amount_${profile.paymentProfileId}`] || 0;

                  const afterTaxAmount =
                    visibleData.find(
                      (item) => item.summaryType === "afterTax",
                    )?.[`amount_${profile.paymentProfileId}`] || 0;

                  if (beforeTaxAmount === 0 && afterTaxAmount === 0)
                    return null;

                  return (
                    <Box
                      key={profile.paymentProfileId}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 0.5,
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          minWidth: "200px",
                        }}
                      >
                        Đợt {index + 1} - {" "}
                        {/* {new Date(profile.paymentDate).toLocaleDateString(
                          "vi-VN",
                        )} */}
                        {formatDate(profile.paymentDate) || "-"}
                      </Typography>
                      <Box
                        sx={{ display: "flex", gap: 3, alignItems: "center" }}
                      >
                        <Box sx={{ textAlign: "right", minWidth: "100px" }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "Inter, sans-serif",
                              fontSize: "0.65rem",
                              color: "text.secondary",
                              display: "block",
                            }}
                          >
                            Trước thuế
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "Inter, sans-serif",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "#1976d2",
                            }}
                          >
                            {formatAmount(beforeTaxAmount)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right", minWidth: "100px" }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "Inter, sans-serif",
                              fontSize: "0.65rem",
                              color: "text.secondary",
                              display: "block",
                            }}
                          >
                            Sau thuế ({profile.vat || contract?.vat || 8}%)
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "Inter, sans-serif",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "#4caf50",
                            }}
                          >
                            {formatAmount(afterTaxAmount)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}

                {/* Tổng lũy kế */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 0.75,
                    backgroundColor: "#f3f4f6",
                    px: 1,
                    borderRadius: 1,
                    border: "1px solid #d1d5db",
                    mt: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: "#374151",
                      minWidth: "200px",
                    }}
                  >
                    📊 Tổng lũy kế thanh toán
                  </Typography>
                  <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
                    <Box sx={{ textAlign: "right", minWidth: "100px" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          fontSize: "0.65rem",
                          color: "text.secondary",
                          display: "block",
                        }}
                      >
                        Trước thuế
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: "#1976d2",
                        }}
                      >
                        {formatAmount(
                          visibleData.find(
                            (item) => item.summaryType === "beforeTax",
                          )?.cumulative_amount || 0,
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right", minWidth: "100px" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          fontSize: "0.65rem",
                          color: "text.secondary",
                          display: "block",
                        }}
                      >
                        Sau thuế
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: "#4caf50",
                        }}
                      >
                        {formatAmount(
                          visibleData.find(
                            (item) => item.summaryType === "afterTax",
                          )?.cumulative_amount || 0,
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.6rem",
                  color: "text.secondary",
                  mt: 1,
                  display: "block",
                }}
              >
                💡 Tính toán dựa trên tổng thanh toán đã nghiệm thu và VAT của
                từng đợt
              </Typography>
            </Box>
          )}
        </Box>

        {!isMobile &&
          !isTablet &&
          detailPanelOpen &&
          selectedDetailItem &&
          renderDetailPanel()}
      </Box>

      {(isMobile || isTablet) && (
        <Drawer
          anchor={isMobile ? "bottom" : "right"}
          open={detailPanelOpen}
          onClose={handleCloseDetailPanel}
          PaperProps={{
            className: isMobile
              ? "contract-detail-drawer-mobile"
              : "contract-detail-drawer-tablet",
          }}
        >
          {renderDetailPanelContent()}
        </Drawer>
      )}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          },
        }}
      >
        {/* <MenuItem
          onClick={() => handleEdit(selectedItem)}
          sx={{
            fontFamily: "Inter, sans-serif",
            fontSize: "0.875rem",
          }}
        >
          <Edit sx={{ mr: 1, fontSize: "1rem" }} />
          Chỉnh sửa
        </MenuItem> */}
        {selectedItem?.level_ < 4 && (
          <MenuItem
            onClick={() => handleAddChild(selectedItem)}
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.875rem",
              opacity: isLeafNode(selectedItem) ? 0.7 : 1,
              fontStyle: isLeafNode(selectedItem) ? "italic" : "normal",
            }}
          >
            <AddBox sx={{ mr: 1, fontSize: "1rem" }} />
            Thêm hạng mục con
            {isLeafNode(selectedItem) && (
              <Typography
                variant="caption"
                sx={{
                  ml: 1,
                  fontSize: "0.7rem",
                  color: "text.secondary",
                }}
              >
                (cần chuyển đổi)
              </Typography>
            )}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => handleDeleteClick(selectedItem)}
          sx={{
            fontFamily: "Inter, sans-serif",
            fontSize: "0.875rem",
            color: "#f44336",
          }}
        >
          <Delete sx={{ mr: 1, fontSize: "1rem" }} />
          Xóa
        </MenuItem>
      </Menu>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              border: "1px solid #e0e0e0",
              boxShadow: "none",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Inter, sans-serif",
            fontSize: "1.1rem",
            fontWeight: 600,
          }}
        >
          Xác nhận xóa hạng mục
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.875rem",
              mb: 2,
            }}
          >
            Bạn có chắc chắn muốn xóa hạng mục "{itemToDelete?.itemName}"?
          </DialogContentText>

          {itemToDelete?.hasChildren && (
            <DialogContentText
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.875rem",
                color: "#f44336",
                fontWeight: 500,
                mb: 2,
                backgroundColor: "#fff5f5",
                padding: 1.5,
                borderRadius: 1,
                border: "1px solid #ffcdd2",
              }}
            >
              ⚠️ Lưu ý: Hạng mục này có chứa các hạng mục con. Việc xóa sẽ xóa
              tất cả hạng mục con.
            </DialogContentText>
          )}

          <DialogContentText
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.8rem",
              color: "#ef6c00",
              backgroundColor: "#fff8e1",
              padding: 1.5,
              borderRadius: 1,
              border: "1px solid #ffcc02",
              mb: 1,
            }}
          >
            🗑️ <strong>Dữ liệu sẽ bị xóa vĩnh viễn:</strong>
            <br />
            • Tất cả thông tin thanh toán liên quan
            <br />
            • Dữ liệu khối lượng theo ngày
            <br />
            {itemToDelete?.hasChildren &&
              "• Toàn bộ cây hạng mục con và dữ liệu liên quan"}
          </DialogContentText>

          <DialogContentText
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.8rem",
              color: "#2e7d32",
              backgroundColor: "#f1f8e9",
              padding: 1.5,
              borderRadius: 1,
              border: "1px solid #81c784",
              mb: 1,
            }}
          >
            💾 <strong>Dữ liệu sẽ được giữ lại:</strong>
            <br />
            • Lịch sử thay đổi hạng mục (để kiểm toán)
            <br />• Thông tin chi tiết về các thay đổi đã thực hiện
          </DialogContentText>

          <DialogContentText
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.75rem",
              color: "#d32f2f",
              fontWeight: 600,
              textAlign: "center",
              mt: 1,
            }}
          >
            ⚠️ Hành động này không thể hoàn tác!
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              color: "text.primary",
            }}
          >
            Hủy
          </AppButton>
          <AppButton
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Xóa
          </AppButton>
        </DialogActions>
      </Dialog>

      {/* Contract Item Modal */}
      {/* Contract Item Modal */}
      <ContractItemModal
        open={modalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        contractId={contractId}
        editMode={modalMode === "edit"}
        itemData={modalEditItem}
        parentItem={modalParentItem}
      />

      <ContractItemsImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        contractId={contractId}
        existingItems={items}
        onImportSuccess={handleImportSuccess}
        onSnackbar={onSnackbar}
      />

      {/* Cannot Add Child Dialog */}
      <Dialog
        open={cannotAddChildDialogOpen}
        onClose={() => setCannotAddChildDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            border: "1px solid #e0e0e0",
            boxShadow: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Inter, sans-serif",
            fontSize: "1.1rem",
            fontWeight: 600,
          }}
        >
          Không thể tạo hạng mục con
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.875rem",
              mb: 2,
            }}
          >
            Hạng mục "{itemCannotAddChild?.itemName}" hiện có đầy đủ thông tin
            thực thi. Đây là một hạng mục thực thi (leaf node) và không thể có
            hạng mục con.
          </DialogContentText>
          <DialogContentText
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#1976d2",
              mb: 1,
            }}
          >
            💡 Để tạo hạng mục con, bạn cần chuyển hạng mục này thành hạng mục
            cha.
          </DialogContentText>
          <DialogContentText
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.8rem",
              color: "#f44336",
              backgroundColor: "#fff3e0",
              padding: 1.5,
              borderRadius: 1,
              border: "1px solid #ffcc80",
            }}
          >
            ⚠️ Lưu ý: Việc chuyển đổi sẽ xóa các thông tin sau:
            <br />
            • Khối lượng, ĐVT, đơn giá, thành tiền
            <br />
            • Ngày bắt đầu, ngày kết thúc
            <br />
            • Trạng thái và tiến độ hoàn thành
            <br />• Khó khăn và độ ưu tiên
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 0.5 }}>
          <AppButton
            variant="outlined"
            onClick={() => setCannotAddChildDialogOpen(false)}
            sx={{
              color: "text.primary",
            }}
          >
            Đóng
          </AppButton>
          <AppButton
            onClick={() => handleConvertToParent(itemCannotAddChild)}
            variant="contained"
          >
            Chuyển thành hạng mục cha
          </AppButton>
        </DialogActions>
      </Dialog>

      {/* Permission Snackbar */}
      <Snackbar
        open={permissionSnackbar.open}
        autoHideDuration={3500}
        onClose={() =>
          setPermissionSnackbar({ ...permissionSnackbar, open: false })
        }
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setPermissionSnackbar({ ...permissionSnackbar, open: false })
          }
          severity={permissionSnackbar.severity}
          sx={{ width: "100%" }}
        >
          {permissionSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContractItemsTable;
